import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar, { menuItems, MODULE_SUBMENUS } from './Sidebar.js';
import Header from './Header.js';
import Footer from './Footer.js';
import PageTransition from './PageTransition.js';
import DashboardPage from './DashboardPage.js';
import PlaceholderPage from './PlaceholderPage.js';
import ClockoraPage from '../clockora-integrated';
import OrgChartPage from '../orgchart/OrgChartPage.js';
import MapPointApp from '../mappoint/MapPointApp.js';
import RouteCoreApp from '../routecore/RouteCoreApp.jsx';
import PayoraApp from '../payoraui/PayoraApp.jsx';
import SettingsPage from './SettingsPage.jsx';
import { ThemeProvider } from './useTheme.js';
import { IntroProvider, default as useIntro } from './useIntro.js';
import EmployeeLandingPage from './EmployeeLandingPage.js';
import MarketingAnnouncements from '../MarketingAnnouncements.js';
import MapViewPage from './MapViewPage.js';
import PCCPage from './PCCPage.js';
import SalesoraPage from './SalesoraPage.js';
import ServiCorePage from './ServiCorePage.js';
import ProjectoraPage from './ProjectoraPage.js';
import ErrorBoundary from './ErrorBoundary.js';

const pageDescriptions = {
  orgchart:
    'Visualize the company hierarchy, reporting lines, departments, and team structures.',
  announcements:
    'Create, manage, and broadcast company-wide announcements, notices, and updates.',
  mapview:
    'Interactive map view for visualizing employee locations, client sites, radius proximity zones, and geospatial workforce distribution.',
  clockora: 'Time tracking, attendance monitoring, and schedule management for your workforce.',
  humacore: 'Human resources management, employee records, and organizational structure.',
  numora: 'Accounting, bookkeeping, financial statements, and general ledger management.',
  payora: 'Payroll processing, compensation management, and financial disbursements.',
  inventora: 'Inventory control, stock tracking, and warehouse management system.',
  pcc: 'Process command center for monitoring and controlling business operations.',
  servicore: 'Service management, customer support tickets, and maintenance scheduling.',
  projectora: 'Project planning, task assignment, milestone tracking, and team collaboration.',
  salesora: 'Sales pipeline, lead management, revenue analytics, and customer relationships.',
  mappoint: 'Geolocation tracking, site mapping, and location-based asset monitoring.',
  routecore: 'Route planning, fleet dispatch optimization, and delivery logistics management.',
  settings: 'Configure your workspace, preferences, and system settings.',
};

function AppInner({ user, onLogout, axiosInstance }) {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [errorBoundaryRetryTick, setErrorBoundaryRetryTick] = useState(0);
  const isIntro = useIntro();

  const effectiveModuleAccess = useMemo(() => {
    const ma = (user && user.module_access) || {};
    const userEmail = (user?.email || '').toLowerCase();
    const SETTINGS_OWNER_EMAILS = new Set(['vinsimon23@gmail.com', 'vinsimon@gmail.com'].map((s) => s.toLowerCase()));
    const isSettingsOwner = SETTINGS_OWNER_EMAILS.has(userEmail);
    const baseRole =
      user?.role === 'admin' || user?.role === 'employee'
        ? user.role
        : 'employee';
    const isSuperAdmin = !!user?.is_super_admin;
    // base role still drives DEFAULT access level for newly-enrolled users
    // (when module_access entry is absent entirely). Once an entry is stored
    // from the Settings Side Panel Access UI, those exact values are obeyed.
    const globalDefaultLevel = isSuperAdmin
      ? 'super_admin'
      : baseRole === 'admin'
      ? 'admin'
      : 'employee';
    const out = {};
    for (const item of menuItems) {
      const entry = ma[item.id];
      const isSettings = item.id === 'settings';
      const hasExplicit = entry && typeof entry === 'object';
      let proposedVisible;
      let effectiveLevel;
      if (isSettings) {
        // Settings visibility strictly via email owner check. is_super_admin
        // does NOT grant Settings visibility without owner email.
        proposedVisible = isSettingsOwner;
        effectiveLevel =
          hasExplicit &&
          typeof entry.access_level === 'string' &&
          ['super_admin', 'admin', 'employee'].includes(entry.access_level)
            ? entry.access_level
            : globalDefaultLevel;
      } else if (hasExplicit && typeof entry.visible === 'boolean') {
        // STRICT OBEY: Settings is the single source of truth. Use the exact
        // stored visible / access_level. No up-clamping to is_super_admin.
        proposedVisible = !!entry.visible;
        effectiveLevel =
          typeof entry.access_level === 'string' &&
          ['super_admin', 'admin', 'employee'].includes(entry.access_level)
            ? entry.access_level
            : globalDefaultLevel;
      } else {
        // No stored explicit entry yet: seed role-based defaults.
        if (isSuperAdmin) proposedVisible = true;
        else if (baseRole === 'admin') proposedVisible = true;
        else proposedVisible = ['dashboard', 'mapview', 'clockora', 'announcements'].includes(item.id);
        effectiveLevel = globalDefaultLevel;
      }
      const declaredSubmenus = MODULE_SUBMENUS[item.id] || [];
      const storedSub = (entry?.submenu_access && typeof entry.submenu_access === 'object') ? entry.submenu_access : {};
      const effectiveSubmenu = {};
      for (const sm of declaredSubmenus) {
        const sv = storedSub[sm.id];
        // Settings submenus: only owner ever sees them.
        const defaultSmVisible = isSettings ? isSettingsOwner : true;
        let smVisible =
          sv && typeof sv === 'object'
            ? sv.visible !== false
            : defaultSmVisible;
        if (isSettings && !isSettingsOwner) smVisible = false;
        effectiveSubmenu[sm.id] = { visible: smVisible };
      }
      out[item.id] = {
        visible: proposedVisible,
        access_level: effectiveLevel,
        submenu_access: effectiveSubmenu,
      };
    }
    return out;
  }, [user]);

  const visibleMenuItems = useMemo(
    () => menuItems.filter((it) => effectiveModuleAccess[it.id]?.visible),
    [effectiveModuleAccess]
  );

  // Guard: if activeMenu is hidden or missing, fall back to the first visible module
  useEffect(() => {
    const targetVisible = effectiveModuleAccess[activeMenu]?.visible;
    if (!targetVisible) {
      const fallback = visibleMenuItems.find((m) => m.id === 'dashboard') || visibleMenuItems[0];
      if (fallback && fallback.id !== activeMenu) setActiveMenu(fallback.id);
    }
  }, [activeMenu, effectiveModuleAccess, visibleMenuItems]);

  const activeMenuItem = menuItems.find((item) => item.id === activeMenu);
  const pageTitle = activeMenuItem?.label || 'Dashboard';

  const handleMenuSelect = useCallback(
    (id) => {
      if (!effectiveModuleAccess[id]?.visible) return;
      setActiveMenu(id);
      setMobileOpen(false);
    },
    [effectiveModuleAccess]
  );

  const handleSwitchToClassic = useCallback(() => {
    navigate('/admin');
  }, [navigate]);

  const handleLogout = useCallback(() => {
    if (typeof onLogout === 'function') onLogout();
    navigate('/login');
  }, [onLogout, navigate]);

  const renderContent = useMemo(() => {
    const userEmail = (user?.email || '').toLowerCase();
    const SETTINGS_OWNER_EMAILS = new Set(['vinsimon23@gmail.com', 'vinsimon@gmail.com'].map((s) => s.toLowerCase()));
    const isSettingsOwner = SETTINGS_OWNER_EMAILS.has(userEmail);

    // Strict access: if this module is hidden by Settings, never render its page.
    // Fallback to first visible module (handled by separate activeMenu useEffect).
    const moduleVisible = !!effectiveModuleAccess[activeMenu]?.visible;
    if (!moduleVisible) {
      return <DashboardPage axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'settings') {
      if (!isSettingsOwner) {
        return <DashboardPage axiosInstance={axiosInstance} />;
      }
      return <SettingsPage user={user} axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'dashboard') {
      return <DashboardPage axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'orgchart') {
      return <OrgChartPage />;
    }

    if (activeMenu === 'announcements') {
      return <MarketingAnnouncements axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'mapview') {
      return <MapViewPage axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'mappoint') {
      return <MapPointApp axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'routecore') {
      return <RouteCoreApp />;
    }

    if (activeMenu === 'payora') {
      return <PayoraApp />;
    }

    if (activeMenu === 'clockora') {
      return <ClockoraPage axiosInstance={axiosInstance} />;
    }

    if (activeMenu === 'pcc') {
      return <PCCPage axiosInstance={axiosInstance} user={user} />;
    }

    if (activeMenu === 'salesora') {
      return <SalesoraPage axiosInstance={axiosInstance} user={user} />;
    }

    if (activeMenu === 'servicore') {
      return <ServiCorePage axiosInstance={axiosInstance} user={user} />;
    }

    if (activeMenu === 'projectora') {
      return <ProjectoraPage axiosInstance={axiosInstance} user={user} />;
    }

    const item = menuItems.find((m) => m.id === activeMenu);
    if (!item) return <DashboardPage axiosInstance={axiosInstance} />;

    return (
      <PlaceholderPage
        title={item.label}
        icon={item.icon}
        description={pageDescriptions[item.id] || 'Explore this module.'}
      />
    );
  }, [activeMenu, axiosInstance, user]);

  return (
    <>
      {isIntro && <div className="intro-curtain" />}

      <div className="intro-app h-screen w-screen flex overflow-hidden relative font-sans">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl float-animation"
            style={{ background: 'var(--nad-blob-1)' }}
          />
          <div
            className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl float-animation"
            style={{ background: 'var(--nad-blob-2)', animationDelay: '3s' }}
          />
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-3xl"
            style={{ background: 'var(--nad-blob-3)' }}
          />
          <div
            className="absolute inset-0"
            style={{
              opacity: 'var(--nad-grid-opacity)',
              backgroundImage:
                'linear-gradient(var(--nad-grid-line) 1px, transparent 1px), linear-gradient(90deg, var(--nad-grid-line) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="intro-sidebar relative z-20 p-4 pr-0 hidden lg:flex flex-shrink-0">
          <Sidebar
            activeMenu={activeMenu}
            onMenuSelect={handleMenuSelect}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
            user={user}
            onLogout={handleLogout}
            axiosInstance={axiosInstance}
          />
        </div>

        {mobileOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />
            <div className="relative z-10 p-4 h-full w-fit">
              <Sidebar
                activeMenu={activeMenu}
                onMenuSelect={handleMenuSelect}
                collapsed={false}
                onToggle={() => setMobileOpen(false)}
                user={user}
                onLogout={handleLogout}
                axiosInstance={axiosInstance}
              />
            </div>
          </div>
        )}

        <div className="flex-1 flex flex-col min-w-0 relative z-10">
          <div className="intro-header">
            <Header
              title={pageTitle}
              onToggleSidebar={() => setMobileOpen(!mobileOpen)}
              axiosInstance={axiosInstance}
              onSwitchToClassic={handleSwitchToClassic}
            />
          </div>

          <ErrorBoundary key={activeMenu + "-" + errorBoundaryRetryTick}>
            <main
              id="main-content-scroll"
              className="flex-1 overflow-y-auto p-6"
              style={{ perspective: '1400px' }}
            >
              <PageTransition contentKey={activeMenu}>{renderContent}</PageTransition>
            </main>
          </ErrorBoundary>

          <div className="intro-footer">
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}

export default function NewAdminDashboard({ user = {}, onLogout, axiosInstance }) {
  return (
    <div className="new-admin-dash">
      <ThemeProvider scopeClass="new-admin-dash">
        <IntroProvider duration={2200}>
          {user.role === 'employee' ? (
            <EmployeeLandingPage user={user} axiosInstance={axiosInstance} onLogout={onLogout} />
          ) : (
            <AppInner user={user} onLogout={onLogout} axiosInstance={axiosInstance} />
          )}
        </IntroProvider>
      </ThemeProvider>
    </div>
  );
}
